<template>
  <div class="live-wrapper">
    <div class="live-video-container">
      <video
        id="video-webrtc"
        ref="videowebrtc"
        controls
        autoplay
        class="live-video"
        @click="emit('video-click')"
      ></video>
      <div v-if="liveInterrupted" class="live-interrupted-overlay">
        <div class="live-interrupted-content">
          <span style="font-size:32px;">📡</span>
          <p style="margin: 8px 0 16px;">Live interrompu</p>
          <a-button type="primary" @click="onRestart">Relancer</a-button>
        </div>
      </div>
    </div>

    <!-- Toggle bar -->
    <div class="live-toggle-bar" @click="showSettings = !showSettings">
      <span>{{ showSettings ? '▲ Masquer les réglages' : '▼ Afficher les réglages' }}</span>
    </div>

    <!-- Settings panel -->
    <div v-show="showSettings" class="live-settings">
      <div class="flex-row flex-justify-center flex-align-center flex-wrap" style="gap: 8px;">
        <template v-if="liveState && isDockLive">
          <span>Lens:</span>
          <a-radio-group v-model:value="lensSelected" button-style="solid">
            <a-radio-button v-for="lens in lensList" :key="lens" :value="lens">{{lens}}</a-radio-button>
          </a-radio-group>
        </template>
        <template v-else>
          <a-select style="width:130px" placeholder="Type" @select="onLiveTypeSelect" v-model:value="livetypeSelected">
            <a-select-option v-for="item in liveTypeList" :key="item.label" :value="item.value">{{ item.label }}</a-select-option>
          </a-select>
          <a-select style="width:150px" placeholder="Drone" v-model:value="droneSelected">
            <a-select-option v-for="item in droneList" :key="item.value" :value="item.value" @click="onDroneSelect(item)">{{ item.label }}</a-select-option>
          </a-select>
          <a-select style="width:130px" placeholder="Caméra" v-model:value="cameraSelected">
            <a-select-option v-for="item in cameraList" :key="item.value" :value="item.value" @click="onCameraSelect(item)">{{ item.label }}</a-select-option>
          </a-select>
        </template>
        <a-select style="width:130px" placeholder="Qualité" @select="onClaritySelect" v-model:value="claritySelected">
          <a-select-option v-for="item in clarityList" :key="item.value" :value="item.value">{{ item.label }}</a-select-option>
        </a-select>
      </div>

      <p class="fz10 mt5" v-if="livetypeSelected == 2">VLC — RTSP: {{ rtspData }}</p>

      <div class="flex-row flex-justify-center flex-align-center mt10" style="gap: 8px;">
        <a-button v-if="liveState && isDockLive" type="primary" @click="onSwitch">Switch Lens</a-button>
        <a-button v-else type="primary" @click="onStart">Play</a-button>
        <a-button type="primary" @click="onStop">Stop</a-button>
        <a-button type="primary" @click="onUpdateQuality">Qualité</a-button>
        <a-button v-if="!liveState || !isDockLive" type="primary" @click="onRefresh">Rafraîchir</a-button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { message } from 'ant-design-vue'
import { onMounted, reactive, ref, watch } from 'vue'
import { CURRENT_CONFIG as config } from '/@/api/http/config'
import { changeLivestreamLens, getLiveCapacity, setLivestreamQuality, startLivestream, stopLivestream } from '/@/api/manage'
import { getRoot } from '/@/root'
import jswebrtc from '/@/vendors/jswebrtc.min.js'
import srs from '/@/vendors/srs.sdk.js'
import store from '/@/store'
import { getDeviceMeta } from '/@/hooks/use-g-map-tsa'

const root = getRoot()
// eslint-disable-next-line no-undef
const emit = defineEmits(['video-click'])

interface SelectOption {
  value: any,
  label: string,
  more?: any
}

const liveTypeList: SelectOption[] = [
  // WEBRTC/WHIP (value 4) retiré : non supporté sur les drones reliés à une RC
  // (chemin Pilot, ex. M3E) — il fait planter DJI Pilot 2. Réservé aux Docks.
  {
    value: 1,
    label: 'RTMP'
  },
  {
    value: 2,
    label: 'RTSP'
  },
  {
    value: 3,
    label: 'GB28181'
  }
]
const clarityList: SelectOption[] = [
  {
    value: 0,
    label: 'Adaptive'
  },
  {
    value: 1,
    label: 'Smooth'
  },
  {
    value: 2,
    label: 'Standard'
  },
  {
    value: 3,
    label: 'HD'
  },
  {
    value: 4,
    label: 'Super Clear'
  }
]

const videowebrtc = ref(null)
const livestreamSource = ref()
const droneList = ref()
const cameraList = ref()
const videoList = ref()
const droneSelected = ref()
const cameraSelected = ref()
const videoSelected = ref()
const videoId = ref()
const liveState = ref<boolean>(false)
const liveInterrupted = ref<boolean>(false)
const livetypeSelected = ref(1) // RTMP par défaut (WebRTC/WHIP = Dock uniquement, plante le M3E)
const claritySelected = ref(3) // HD par défaut
const showSettings = ref<boolean>(true)
const rtspData = ref()
const lensList = ref<string[]>([])
const lensSelected = ref<String>()
const isDockLive = ref(false)
const nonSwitchable = 'normal'
let webrtc: any = null

const onRefresh = async () => {
  droneList.value = []
  cameraList.value = []
  videoList.value = []
  droneSelected.value = null
  cameraSelected.value = null
  videoSelected.value = null
  await getLiveCapacity({})
    .then(res => {
      console.log(res)
      if (res.code === 0) {
        if (res.data === null) {
          console.warn('warning: get live capacity is null!!!')
          return
        }
        const resData: Array<[]> = res.data
        console.log('live_capacity:', resData)
        livestreamSource.value = resData

        const temp: Array<SelectOption> = []
        if (livestreamSource.value) {
          livestreamSource.value.forEach((ele: any) => {
            temp.push({ label: ele.name + '-' + ele.sn, value: ele.sn, more: ele.cameras_list })
          })
          droneList.value = temp
          // Auto-sélection du premier drone et de sa première caméra
          if (temp.length > 0) {
            onDroneSelect(temp[0])
            if (cameraList.value && cameraList.value.length > 0) {
              onCameraSelect(cameraList.value[0])
            }
          }
        }
      }
    })
    .catch(error => {
      message.error(error)
      console.error(error)
    })
}

onMounted(() => {
  watch(() => store.state.livestreamOthersVisible, (visible) => {
    if (visible) onRefresh()
  })
})
const onStart = async () => {
  console.log(
    'Param:',
    livetypeSelected.value,
    droneSelected.value,
    cameraSelected.value,
    videoSelected.value,
    claritySelected.value
  )
  const timestamp = new Date().getTime().toString()
  if (
    livetypeSelected.value == null ||
    droneSelected.value == null ||
    cameraSelected.value == null ||
    claritySelected.value == null
  ) {
    message.warn('waring: not select live para!!!')
    return
  }
  videoId.value =
    droneSelected.value + '/' + cameraSelected.value + '/' + (videoSelected.value || nonSwitchable + '-0')

  let liveURL = ''
  switch (livetypeSelected.value) {
    case 1: {
      // RTMP
      liveURL = config.rtmpURL + timestamp
      break
    }
    case 2: {
      // RTSP
      liveURL = `userName=${config.rtspUserName}&password=${config.rtspPassword}&port=${config.rtspPort}`
      break
    }
    case 3: {
      liveURL = `serverIP=${config.gbServerIp}&serverPort=${config.gbServerPort}&serverID=${config.gbServerId}&agentID=${config.gbAgentId}&agentPassword=${config.gbPassword}&localPort=${config.gbAgentPort}&channel=${config.gbAgentChannel}`
      break
    }
    case 4: {
      break
    }
    default:
      console.warn('warning: live type is not correct!!!')
      break
  }
  await startLivestream({
    url: liveURL,
    video_id: videoId.value,
    url_type: livetypeSelected.value,
    video_quality: claritySelected.value
  })
    .then(res => {
      if (res.code !== 0) {
        return
      }
      if (livetypeSelected.value === 3) {
        const url = res.data.url
        const videoElement = videowebrtc.value
        // gb28181,it will fail if not wait.
        message.loading({
          content: 'Loding...',
          duration: 4,
          onClose () {
            const player = new jswebrtc.Player(url, {
              video: videoElement,
              autoplay: true,
              onPlay: (obj: any) => {
                console.log('start play livestream')
              }
            })
          }
        })
      } else if (livetypeSelected.value === 2) {
        console.log(res)
        rtspData.value = 'url:' + res.data.url
      } else if (livetypeSelected.value === 1) {
        const url = res.data.url
        const videoElement = videowebrtc.value as unknown as HTMLMediaElement
        console.log('start live (RTMP→WHEP):', url)
        videoElement.muted = true
        // Poll WHEP until stream is available (max 8s, retry every 500ms)
        let attempts = 0
        const tryPlay = () => {
          attempts++
          webrtc = new srs.SrsRtcWhipWhepAsync()
          videoElement.srcObject = webrtc.stream
          webrtc.play(url).then(function (session: any) {
            liveState.value = true
            showSettings.value = false
            console.info('WHEP connected after', attempts, 'attempt(s)')
          }).catch(function (reason: any) {
            webrtc.close()
            if (attempts < 16) {
              setTimeout(tryPlay, 500)
            } else {
              liveState.value = false
              liveInterrupted.value = true
              console.error('WHEP failed after', attempts, 'attempts:', reason)
            }
          })
        }
        liveState.value = true
        showSettings.value = false
        tryPlay()
      } else if (livetypeSelected.value === 4) {
        const videoElement = videowebrtc.value as unknown as HTMLMediaElement
        videoElement.muted = true
        playWebrtc(videoElement, res.data.url)
      }
      liveState.value = true
      showSettings.value = false
      // Recentrer la carte sur le drone
      const markers = store.state.markerInfo.coverMap
      const marker = markers[droneSelected.value]
      if (marker) {
        const map = root.$map
        if (map) {
          map.flyTo({ center: marker.getLngLat(), zoom: Math.max(map.getZoom(), 15), speed: 1.5 })
        }
      }
      // Show OSD panel for this drone
      const meta = getDeviceMeta(droneSelected.value)
      if (meta) {
        const { device, isDock } = meta
        store.commit('SET_OSD_VISIBLE_INFO', {
          sn: device.sn,
          callsign: device.callsign,
          model: device.model,
          visible: true,
          gateway_sn: device.gateway.sn,
          is_dock: isDock,
          gateway_callsign: device.gateway.callsign,
          payloads: device.payload
        })
      } else {
        // Fallback : ouvrir l'OSD avec les infos disponibles depuis droneList
        const droneItem = droneList.value?.find((d: any) => d.value === droneSelected.value)
        if (droneItem) {
          store.commit('SET_OSD_VISIBLE_INFO', {
            sn: droneSelected.value,
            callsign: droneItem.label,
            model: droneItem.label,
            visible: true,
            gateway_sn: droneSelected.value,
            is_dock: false,
            gateway_callsign: droneItem.label,
            payloads: []
          })
        }
      }
    })
    .catch(err => {
      console.error(err)
    })
}
const onStop = () => {
  videoId.value =
    droneSelected.value + '/' + cameraSelected.value + '/' + (videoSelected.value || nonSwitchable + '-0')

  // Couper immédiatement le WebRTC et vider la vidéo
  if (webrtc) {
    webrtc.close()
    webrtc = null
  }
  const videoElement = videowebrtc.value as unknown as HTMLMediaElement
  if (videoElement) {
    videoElement.srcObject = null
  }

  stopLivestream({
    video_id: videoId.value
  }).then(res => {
    if (res.code === 0) {
      message.success(res.message)
      liveState.value = false
      liveInterrupted.value = false
      lensSelected.value = undefined
      console.log('stop play livestream')
    }
  })
}

const onUpdateQuality = () => {
  if (!liveState.value) {
    message.info('Please turn on the livestream first.')
    return
  }
  setLivestreamQuality({
    video_id: videoId.value,
    video_quality: claritySelected.value
  }).then(res => {
    if (res.code === 0) {
      message.success('Set the clarity to ' + clarityList[claritySelected.value].label)
    }
  })
}

const onLiveTypeSelect = (val: any) => {
  livetypeSelected.value = val
}
const onDroneSelect = (val: SelectOption) => {
  droneSelected.value = val.value
  const temp: Array<SelectOption> = []
  cameraList.value = []
  cameraSelected.value = undefined
  videoSelected.value = undefined
  videoList.value = []
  lensList.value = []
  if (!val.more) {
    return
  }
  val.more.forEach((ele: any) => {
    temp.push({ label: ele.name, value: ele.index, more: ele.videos_list })
  })
  cameraList.value = temp
}
const onCameraSelect = (val: SelectOption) => {
  cameraSelected.value = val.value
  const result: Array<SelectOption> = []
  videoSelected.value = undefined
  videoList.value = []
  lensList.value = []
  if (!val.more) {
    return
  }

  val.more.forEach((ele: any) => {
    result.push({ label: ele.type, value: ele.index, more: ele.switch_video_types })
  })
  videoList.value = result
  if (videoList.value.length === 0) {
    return
  }
  const firstVideo: SelectOption = videoList.value[0]
  videoSelected.value = firstVideo.value
  lensList.value = firstVideo.more
  lensSelected.value = firstVideo.label
  isDockLive.value = lensList.value?.length > 0
}
const onVideoSelect = (val: SelectOption) => {
  videoSelected.value = val.value
  lensList.value = val.more
  lensSelected.value = val.label
}
const onClaritySelect = (val: any) => {
  claritySelected.value = val
}
const onSwitch = () => {
  if (lensSelected.value === undefined || lensSelected.value === nonSwitchable) {
    message.info('The ' + nonSwitchable + ' lens cannot be switched, please select the lens to be switched.', 8)
    return
  }
  changeLivestreamLens({
    video_id: videoId.value,
    video_type: lensSelected.value
  }).then(res => {
    if (res.code === 0) {
      message.success('Switching live camera successfully.')
    }
  })
}
const playWebrtc = (videoElement: HTMLMediaElement, url: string) => {
  if (webrtc) {
    webrtc.close()
  }
  liveInterrupted.value = false
  webrtc = new srs.SrsRtcWhipWhepAsync()
  videoElement.srcObject = webrtc.stream

  // Détecter coupure via l'état de la connexion WebRTC
  webrtc.pc?.addEventListener('connectionstatechange', () => {
    const state = webrtc.pc?.connectionState
    if (state === 'disconnected' || state === 'failed' || state === 'closed') {
      if (liveState.value) {
        liveState.value = false
        liveInterrupted.value = true
      }
    }
  })

  // Détecter coupure via la balise video
  videoElement.addEventListener('emptied', () => {
    if (liveState.value) {
      liveState.value = false
      liveInterrupted.value = true
    }
  }, { once: true })

  webrtc.play(url).then(function (session: any) {
    console.info(session)
  }).catch(function (reason: any) {
    webrtc.close()
    liveState.value = false
    liveInterrupted.value = true
    console.error(reason)
  })
}

const onRestart = () => {
  liveInterrupted.value = false
  onStart()
}
</script>

<style lang="scss" scoped>
@import '/@/styles/index.scss';
.live-wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
}
.live-video-container {
  position: relative;
  width: 100%;
}
.live-video {
  width: 100%;
  height: auto;
  aspect-ratio: 16/9;
  display: block;
  background: #000;
}
.live-interrupted-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}
.live-interrupted-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #fff;
}
.live-toggle-bar {
  background: #1a1a1a;
  color: #aaa;
  font-size: 12px;
  text-align: center;
  padding: 4px 0;
  cursor: pointer;
  user-select: none;
  &:hover { color: #fff; background: #2a2a2a; }
}
.live-settings {
  padding: 10px;
  background: #232323;
}
</style>
